import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { FiArrowRight } from 'react-icons/fi';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import data from '../../JsonFolders/portfolio.json';
import { projectSlug } from '../../data/profile';
import TechStack from './TechStack';

gsap.registerPlugin(ScrollTrigger);

const cases = [
  {
    project: data.images.projects[0],
    eyebrow: 'Computer vision · Elections',
    problem: 'A ranked-preference ballot has to be read accurately, and uncertain marks need a human decision.',
    built: 'A pipeline that straightens scans, verifies the stamp with YOLO, reads handwritten preferences with TensorFlow, then checks the sequence against election rules.',
    result: 'A reasoned Valid or Doubtful verdict for every ballot, with JSON and CSV audit records. Staff can run it from a scanner, a phone, the command line or AWS.',
    steps: ['Prepare image', 'Verify stamp', 'Read preferences', 'Review exceptions'],
  },
  {
    project: data.images.projects[1],
    eyebrow: 'Marine video · Applied AI',
    problem: 'Underwater footage is slow to inspect by hand when fish must be found, followed and identified.',
    built: 'Aithne, built around VIAME with Python, FastAPI, PostgreSQL, React and Docker, processes video and puts the results in a dashboard people can review.',
    result: 'It detects, tracks, counts and identifies fish in recorded video and on 17 live ocean cameras.',
    steps: ['Video input', 'Detect fish', 'Track & count', 'Review results'],
    // The image is the project's logo rather than a screenshot: show it at logo size,
    // with the system's scale beside it (figures from the write-up in profile.js)
    figures: [
      ['17', 'live ocean cameras monitored'],
      ['3', 'Docker services: API, GPU worker, live worker'],
      ['~30', 'pytest modules that run without a GPU'],
    ],
  },
];

// "AABCI - AI Automated Ballot Counter Ireland" -> short name up top, full name underneath
const splitTitle = (title) => {
  const [name, ...rest] = title.split(' - ');
  return [name, rest.join(' - ')];
};

export default function FeaturedCases() {
  const root = useRef(null);

  useEffect(() => {
    const media = window.matchMedia('(prefers-reduced-motion: reduce)');
    let context;
    const setup = () => {
      context?.revert();
      root.current.querySelectorAll('.case-step').forEach((step) => step.classList.toggle('is-lit', media.matches));
      if (media.matches) return;
      context = gsap.context(() => {
        root.current.querySelectorAll('.case-visual').forEach((visual) => {
          gsap.fromTo(visual.querySelector('img'),
            { clipPath: 'inset(7% 7% 7% 7%)', scale: 1.12 },
            { clipPath: 'inset(0% 0% 0% 0%)', scale: 1, ease: 'none', scrollTrigger: { trigger: visual, start: 'top 92%', end: 'top 35%', scrub: true } }
          );
          // The line draws across the workflow and each step lights up as it is reached
          const steps = [...visual.querySelectorAll('.case-step')];
          gsap.fromTo(visual.querySelector('.case-flow-line'), { scaleX: 0 }, {
            scaleX: 1,
            ease: 'none',
            scrollTrigger: {
              trigger: visual,
              start: 'top 70%',
              end: 'bottom 55%',
              scrub: true,
              onUpdate: ({ progress }) => steps.forEach((step, i) => step.classList.toggle('is-lit', progress > i / steps.length + 0.02)),
            },
          });
        });
      }, root);
    };
    setup();
    media.addEventListener('change', setup);
    return () => { media.removeEventListener('change', setup); context?.revert(); };
  }, []);

  return (
    <div ref={root} className="featured-cases">
      {cases.map(({ project, eyebrow, problem, built, result, steps, figures }) => {
        const [name, fullName] = splitTitle(project.title);
        return (
          <article className="case" key={project.title}>
            <div className="case-copy" data-reveal>
              <p className="case-eyebrow">{eyebrow}</p>
              <h3>{name}{fullName && <span className="case-fullname">{fullName}</span>}</h3>
              {project.highlight && <p className="case-context">{project.highlight}</p>}
              <dl className="case-facts">
                <div><dt>The problem</dt><dd>{problem}</dd></div>
                <div><dt>What I built</dt><dd>{built}</dd></div>
                <div className="is-result"><dt>The result</dt><dd>{result}</dd></div>
              </dl>
              <TechStack stack={project.stack} className="case-stack" />
              <Link className="text-link case-link" to={`/projects/${projectSlug(project.title)}`}>
                Read the full write-up <FiArrowRight className="icon-right" aria-hidden="true" />
              </Link>
            </div>
            <div className="case-visual" data-reveal style={{ '--i': 1 }}>
              <div className={`case-image-wrap ${figures ? 'is-logo' : ''}`}>
                <img src={project.src} alt={project.alt} loading="lazy" decoding="async" width="780" height="488" />
                {figures && (
                  <dl className="case-figures">
                    {figures.map(([value, label]) => (
                      <div key={label}><dt className="tabular">{value}</dt><dd>{label}</dd></div>
                    ))}
                  </dl>
                )}
              </div>
              <div className="case-flow">
                <span className="case-flow-line" aria-hidden="true" />
                <ol className="case-steps" aria-label={`How ${name} works`}>
                  {steps.map((step, i) => (
                    <li className="case-step" key={step}><b className="tabular">{String(i + 1).padStart(2, '0')}</b>{step}</li>
                  ))}
                </ol>
              </div>
            </div>
          </article>
        );
      })}
    </div>
  );
}
